import type { CSSProperties } from 'react';
import React, { useEffect } from 'react';

import Spinner from '@atlaskit/spinner/spinner';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import type { XCSS } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { AnimatedLoadingGradient } from '@atlassian/ai-image-generation/animated-loading-gradient';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import { EditorPageCoverPictureEntry } from '@confluence/page-cover-picture/entry-points/EditorPageCoverPictureEntry.ts';

import type { CoverPictureAndButtonProps } from '../titleareaContentComponentsTypes.ts';

const coverPictureAndButtonWithCoverPictureAreaBoxStyles = xcss({
	width: '100%',
	marginBottom: 'space.100',
});

const coverPictureAndButtonWithoutCoverPictureAreaBoxStyles = xcss({
	width: 'unset',
	marginBottom: 'unset',
});

const coverPictureAndButtonWithFloatingToolbarBoxStyles = xcss({
	marginRight: 'space.0',
	display: 'flex',
	justifyContent: 'center',
});

const coverPictureAndButtonWithFloatingToolbarAndUploadingBoxStyles = xcss({
	opacity: 0,
});

const coverPictureAndButtonWithoutFloatingToolbarAndUploadingBoxStyles = xcss({
	opacity: 1,
});

const uploadingPictureBoxStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	background: 'color.background.accent.gray.subtlest',
	borderRadius: 'radius.small',
	position: 'relative',
});

export const CoverPictureAndButton = ({
	contentId,
	contentType,
	isPublishingContent,
	userOffline,
	hasCoverPicture,
	isUploadingPicture,
	isUnpublishedDraft,
	floatingToolbarButtonStyle,
	isFixedWidthImageOption,
	updateHeight,
	currentHeight,
	isReadOnly,
	isDraftLockedForEditing,
	spaceKey,
	isUnsplash,
	dynamicCoverImageHeight,
	width,
	coverImageWidth,
	aiHeaderImageStatus,
	aiHeaderImageFileId,
	aiHeaderImageError,
	generateAIHeaderImage,
	setAIHeaderImageStatus,
	isSmartCreate,
}: CoverPictureAndButtonProps): React.JSX.Element => {
	const heightChangeThreshold = 4;

	useEffect(() => {
		if (
			updateHeight &&
			currentHeight &&
			dynamicCoverImageHeight &&
			Math.abs(currentHeight - dynamicCoverImageHeight) > heightChangeThreshold
		) {
			updateHeight(dynamicCoverImageHeight);
		}
	}, [updateHeight, currentHeight, dynamicCoverImageHeight]);

	const coverPictureAndButtonXcss: Array<XCSS> = [];
	if (isUploadingPicture || hasCoverPicture) {
		coverPictureAndButtonXcss.push(coverPictureAndButtonWithCoverPictureAreaBoxStyles);
	} else {
		coverPictureAndButtonXcss.push(coverPictureAndButtonWithoutCoverPictureAreaBoxStyles);
	}
	coverPictureAndButtonXcss.push(coverPictureAndButtonWithFloatingToolbarBoxStyles);
	if (isUploadingPicture) {
		coverPictureAndButtonXcss.push(coverPictureAndButtonWithFloatingToolbarAndUploadingBoxStyles);
	} else {
		coverPictureAndButtonXcss.push(
			coverPictureAndButtonWithoutFloatingToolbarAndUploadingBoxStyles,
		);
	}

	const isGeneratingHeaderImage =
		aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW';

	const uploadingPictureInlineCss: CSSProperties = {
		width: width && !isFixedWidthImageOption ? `${width}px` : '100%',
		height: `${dynamicCoverImageHeight}px`,
		position: isFixedWidthImageOption ? 'relative' : 'absolute',
		// This is a dynamic value so tokens cannot be used here
		left: '0px',
	};

	const LoadingIndicator = () => {
		// isGeneratingHeaderImage will always be false when the experiment is off,
		// but evaluating here anyway for additional safety
		if (
			isGeneratingHeaderImage &&
			(isExperimentEnabled('cc_page_experiences_ai_header_image_generation') ||
				isExperimentEnabled('cc_page_experiences_aifc_auto_generated_header_img'))
		) {
			return <AnimatedLoadingGradient />;
		}

		return <Spinner size="medium" testId="CoverPictureAndButton-Spinner" />;
	};

	return (
		<>
			{isUploadingPicture && (
				<Box
					xcss={uploadingPictureBoxStyles}
					testId="uploading-picture-wrapper"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={uploadingPictureInlineCss}
				>
					<LoadingIndicator />
				</Box>
			)}
			{/* eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage */}
			<Box xcss={coverPictureAndButtonXcss} testId="cover-picture-and-button-wrapper">
				<EditorPageCoverPictureEntry
					contentId={contentId}
					isPublishingContent={isPublishingContent}
					contentType={contentType}
					userOffline={userOffline}
					isUnpublishedDraft={isUnpublishedDraft}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					isFixedWidthImageOption={isFixedWidthImageOption}
					width={coverImageWidth}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					isReadOnly={isReadOnly || !!isDraftLockedForEditing}
					spaceKey={spaceKey}
					hasCoverPicture={hasCoverPicture}
					isUnsplash={isUnsplash}
					aiHeaderImageStatus={aiHeaderImageStatus}
					aiHeaderImageFileId={aiHeaderImageFileId}
					aiHeaderImageError={aiHeaderImageError}
					generateAIHeaderImage={generateAIHeaderImage}
					setAIHeaderImageStatus={setAIHeaderImageStatus}
					isSmartCreate={isSmartCreate}
				/>
			</Box>
		</>
	);
};
